import { authApi } from './auth.js';
import { authStore, clearPendingLogout, getPendingLogoutTarget, hasPendingLogout, markPendingLogout, sessionFromTokens, useAuthStore, } from './auth-store.js';
import { configureClient } from './client.js';
import { ApiError } from './errors.js';
// Re-exported so existing importers (this module's own contract, tests)
// keep working — the implementation now lives in auth-store.ts, the only
// place a session is ever installed (see that module for why).
export { hasPendingLogout } from './auth-store.js';
let configured = false;
/**
 * attemptRevoke — the actual server round-trip `revokeRefreshToken` and the
 * pending-logout flush (inside `performRefresh`, below) both drive. When no
 * token is passed, it first recovers the CURRENT refresh token — purely to
 * have something to hand `/auth/logout` (unlike `/auth/refresh`, the
 * backend's `LogoutDto` still requires a `refreshToken` in the body and has
 * no cookie-reading fallback of its own; this fix deliberately never
 * touches apps/backend).
 *
 * MEDIUM fix (adversarial review, findings #3+#4) — that recovery no longer
 * calls `authApi.refresh({})` directly. By default it goes through
 * `runRefresh()` instead, so it (a) shares the SAME lock/single-flight as
 * every other refresh attempt — it can no longer land in the gap between
 * another tab's in-flight rotation and that tab's own response — and (b)
 * inherits `performRefresh`'s cookie-first-then-token-fallback logic for
 * free, recovering the freshest rotated token even when ANOTHER tab did the
 * last rotation. A successful recovery installs the session into the store
 * (that's `performRefresh`'s normal job) — since this call only exists to
 * revoke it, `authStore.signOut()` immediately undoes that install (a quiet
 * clear, not `expire()` — this is a deliberate sign-out, never a real
 * expiry, so `sessionExpired` must stay untouched).
 *
 * `recover` is overridable — `performRefresh`'s OWN pending-logout flush
 * (below) calls this with `rawRefresh` instead of the default `runRefresh`:
 * that flush already runs INSIDE `runRefresh()`'s single-flight, so
 * recovering via `runRefresh()` again there would await a promise
 * (`refreshInflight`) that only resolves once this same still-running call
 * returns — a self-referential deadlock. `rawRefresh` gives it the exact
 * same cookie-first/token-fallback network logic without re-entering a lock
 * it already holds.
 *
 * Returns `'retry'` only for a network-shaped failure (offline, the backend
 * unreachable) — an outcome the caller should remember and flush later; any
 * other outcome (including "nothing to revoke" — no cookie, no token) is
 * `'done'`.
 */
async function attemptRevoke(refreshToken, recover = runRefresh) {
    try {
        let rt = refreshToken;
        if (!rt) {
            const outcome = await recover();
            if (outcome.kind === 'unreachable')
                return 'retry';
            if (outcome.kind === 'unauthenticated')
                return 'done'; // nothing to revoke
            rt = outcome.session.refreshToken;
            authStore.signOut();
        }
        await authApi.logout({ refreshToken: rt });
        return 'done';
    }
    catch (err) {
        return classifyBootstrapFailure(err) === 'unreachable' ? 'retry' : 'done';
    }
}
/**
 * revokeRefreshToken — MEDIUM fix (adversarial review): `signOut`/
 * `expireSession` from the offline-shell (an EMPTY in-memory store — no
 * refresh token at all) used to be a complete server-side no-op (`if
 * (!refreshToken) return;`) — the httpOnly `jq4_refresh` cookie survived
 * untouched, so the NEXT boot (or the CRITICAL-fix cookie-fallback refresh)
 * silently restored the very session the operator just tried to sign out
 * of. Now always talks to the server (`attemptRevoke`, above); when that
 * genuinely can't reach the network, remembers the intent
 * (`markPendingLogout`) — tagged with `sid`, the session id this revoke
 * targets (HIGH fix, finding #1) — so `performRefresh` flushes it before
 * ever recovering a session from the cookie again, and never revokes a
 * DIFFERENT (newer) session that happens to be live when it flushes.
 * Exported for direct unit testing.
 */
export async function revokeRefreshToken(refreshToken, sid = null) {
    const result = await attemptRevoke(refreshToken);
    if (result === 'retry') {
        markPendingLogout(sid);
    }
    else {
        clearPendingLogout();
    }
}
// A hanging backend (no response at all — arguably worse than a fast 5xx)
// must not leave the valet staring at a blank splash screen forever.
const BOOTSTRAP_TIMEOUT_MS = 8_000;
// fix/refresh-rotation-lost-response (develop #167, security review finding
// 3), folded into the single-flight pipeline below instead of kept as a
// second, parallel refresh implementation — a 409 refresh_in_progress from
// POST /auth/refresh means the SERVER lost a compare-and-swap race for the
// presented token: a genuinely concurrent second request for the SAME token
// (two tabs, a bootstrap call racing a 401-retry, or a caller racing this
// very single-flight's own loser before it even reaches the lock). It is
// NOT a dead/invalid credential — a sibling request is just mid-flight
// rotating it right now. `refreshWithRetry` (below `requestRefresh`) waits a
// short jitter and retries EXACTLY ONCE, preferring the httpOnly cookie;
// `handleRefreshFailure` and `classifyBootstrapFailure` are what keep a 409
// — even a persistent one, after that retry — from ever being treated as
// evidence the session is invalid (never expire(), never log out).
function refreshInProgressDelayMs() {
    // 300-800ms jitter — enough for the winning concurrent request's response
    // (and its Set-Cookie) to land before we retry.
    return 300 + Math.floor(Math.random() * 500);
}
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
function isRefreshInProgress(err) {
    return err instanceof ApiError && err.status === 409 && err.code === 'refresh_in_progress';
}
/**
 * Pure classifier — exported for direct unit testing (see use-auth.spec.ts).
 * `err` is whatever `authApi.refresh()` rejected with; `client.ts`'s
 * `request()` always wraps failures as `ApiError`, so anything else here is
 * defensive (kept conservative: 'unauthenticated', never 'unreachable').
 */
export function classifyBootstrapFailure(err) {
    if (!(err instanceof ApiError))
        return 'unauthenticated';
    // The fetch itself never got a response — network_error covers a real
    // connectivity failure AND an AbortController timeout (both throw the
    // same ApiError shape from client.ts's request(), with no `status`).
    if (err.code === 'network_error')
        return 'unreachable';
    // A 409 refresh_in_progress is contention, not a rejection (see above).
    // Persistent contention (still 409 after refreshWithRetry's one jittered
    // retry) maps to 'unreachable': the server never actually said no, so
    // callers must treat it exactly like an unreachable backend.
    if (isRefreshInProgress(err))
        return 'unreachable';
    const status = err.status;
    if (status !== undefined && status >= 500 && status <= 599)
        return 'unreachable';
    if (status === 429)
        return 'unreachable';
    return 'unauthenticated';
}
// ── Single-flight refresh across BOTH paths — HIGH fix (adversarial review)
//
// BEFORE this fix there were TWO independently-guarded refresh paths: the
// in-memory-token branch below (`setupAuthClient`'s `refresh` callback)
// called `authApi.refresh({refreshToken})` directly on every 401 with NO
// de-duplication, while the cookie-fallback path had its OWN separate
// `bootstrapInflight` guard. N concurrent 401s (e.g. several outbox items
// sent in parallel by the foreground drainer) each independently refreshed
// with the SAME (about-to-rotate) token — the backend's rotation means only
// the FIRST to land actually wins; every LOSER got a 401 back and (before
// this fix) unconditionally called `authStore.expire()`, wiping out the
// session the winner had just installed a moment earlier.
//
// Fix: ONE shared in-flight promise (`refreshInflight`) for BOTH paths —
// `performRefresh()` decides which branch to take (in-memory token vs.
// cookie) by reading the store itself, at the START of the attempt, so
// every caller (regardless of which path led them here) shares the exact
// same network call and result. Wrapped in
// `navigator.locks.request('jq4-refresh', ...)` when available so the
// single-flight guarantee also holds ACROSS TABS/windows on the same
// origin (the PWA installed standalone + a browser tab open to the same
// origin is a realistic same-device scenario) — with a graceful
// in-process-only fallback when the Web Locks API is unavailable (Safari
// < 15.4).
let refreshInflight = null;
// In-memory-token fallback attempt — HIGH fix (adversarial review, finding
// #2). Previously NO signal was ever threaded through this branch at all;
// a hung request here held the exclusive `jq4-refresh` lock forever,
// blocking refresh in every tab/window indefinitely.
const TOKEN_REFRESH_TIMEOUT_MS = 10_000;
// How long a caller is willing to WAIT to ACQUIRE the `jq4-refresh` lock
// before giving up — HIGH fix (finding #2). The worst-case legitimate hold
// time is the cookie attempt's full timeout PLUS the token-fallback's full
// timeout run sequentially (BOOTSTRAP_TIMEOUT_MS + TOKEN_REFRESH_TIMEOUT_MS
// = 18s) — this is set comfortably above that, with margin, so waiting out
// an in-progress, correctly-bounded refresh in ANOTHER tab is exactly
// right; only a lock STILL held past that point (a bug, a hung callback
// that somehow bypassed both fetch timeouts) should give up on waiting.
// `ifAvailable` is deliberately NOT used — unlike the offline-drainer's own
// lock, skipping here would just 401 the caller instead of getting a fresh
// token, so waiting (bounded) is correct.
//
// NOTE: `refreshWithRetry`'s 409 retry shares ONE deadline with the attempt
// it retries (the retry only gets the remaining budget), so the 18s
// worst case above still holds with the retry included.
const REFRESH_LOCK_WAIT_TIMEOUT_MS = 20_000;
/**
 * Runs `fn` with an AbortSignal that aborts after `ms`. Deliberately a
 * plain `AbortController` + `setTimeout` — never `AbortSignal.timeout` —
 * for two reasons: (1) it stays testable under `vi.useFakeTimers()` (the
 * native static method runs on its own internal timer, invisible to
 * fake-timer frameworks — see apps/pwa's own abort-signal-compat tests,
 * which have to delete the native method to get fake-timer control at
 * all), and (2) it needs no feature detection to already be safe on
 * pre-16.0 iOS/Safari, which lacks `AbortSignal.timeout` entirely.
 */
async function withTimeout(ms, fn) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ms);
    try {
        return await fn(controller.signal);
    }
    finally {
        clearTimeout(timeoutId);
    }
}
function requestRefresh(body, timeoutMs) {
    return withTimeout(timeoutMs, (signal) => authApi.refresh(body, { signal }));
}
/**
 * Wraps a single refresh attempt with the 409 retry contract (see the
 * refreshInProgressDelayMs/isRefreshInProgress block above): on
 * refresh_in_progress, wait briefly and retry EXACTLY ONCE, preferring the
 * httpOnly cookie (body-less) regardless of what body the first attempt
 * sent — the winning concurrent request's fresh Set-Cookie will be there by
 * then. The retry only gets whatever is LEFT of the original attempt's
 * timeout budget (one shared deadline for both requests), so a branch never
 * holds the lock longer than its own timeout; it runs still inside whichever lock/single-flight is already held by the caller
 * (runRefreshWithLock/runRefresh) — this never acquires a lock of its own.
 * Any other error — including a 409 that persists through the retry —
 * propagates to the caller unchanged.
 */
async function refreshWithRetry(body, timeoutMs) {
    const deadline = Date.now() + timeoutMs;
    try {
        return await requestRefresh(body, timeoutMs);
    }
    catch (err) {
        if (!isRefreshInProgress(err))
            throw err;
        await sleep(refreshInProgressDelayMs());
        const remainingMs = deadline - Date.now();
        // Budget spent by the first attempt + jitter: surface the original 409
        // (classified 'unreachable', never expire()) instead of overrunning.
        if (remainingMs <= 0)
            throw err;
        return requestRefresh({}, remainingMs);
    }
}
function installRefreshedSession(next) {
    const session = sessionFromTokens(next);
    if (!session)
        return { kind: 'unauthenticated' };
    const prev = authStore.get();
    // Keep the identity we already had, but fill a missing email from the refresh
    // response (cold bootstrap after a reload has no previous user at all).
    const user = prev?.user ?? session.user;
    authStore.set({ ...session, user: { ...user, email: user.email ?? session.user.email } });
    return { kind: 'session', session };
}
// Never wipe a session the store has since moved on to (a WINNING parallel
// refresh that already installed a newer session, or a fresh sign-in) just
// because THIS now-stale attempt failed — `sentRefreshToken` is the exact
// token (if any) the failed attempt spent, captured before the round-trip.
function handleRefreshFailure(err, sentRefreshToken) {
    if (isRefreshInProgress(err)) {
        // Persistent contention — survived refreshWithRetry's one retry and
        // still came back 409. The token is NOT invalid; a sibling request is
        // still mid-rotation. Never expire() on this, regardless of whether the
        // store still holds the token this attempt spent.
        return { kind: 'unreachable' };
    }
    // QA-I11 — the server said WHY (organization suspended). Carry the reason into
    // the store so the login screen can say so. Same "never wipe a newer session"
    // guard as the generic path; additionally applies on a cold bootstrap (no
    // in-memory token and still no session) where there is nothing to wipe but the
    // user still needs the explanation.
    const suspended = err instanceof ApiError && err.code === 'organization_suspended';
    const sameSession = sentRefreshToken !== null && authStore.getRefreshToken() === sentRefreshToken;
    if (suspended && (sameSession || (sentRefreshToken === null && authStore.get() === null))) {
        authStore.expire('organization_suspended');
    }
    else if (sameSession) {
        authStore.expire();
    }
    return { kind: classifyBootstrapFailure(err) };
}
/**
 * The actual network refresh attempt — no pending-logout handling, no lock.
 */
async function rawRefresh() {
    // Read the in-memory refresh token ONCE, before the network round-trip —
    // this is the exact token (if any) THIS attempt might fall back to.
    const sentRefreshToken = authStore.getRefreshToken();
    // MEDIUM fix (adversarial review, finding #4) — ALWAYS try the httpOnly
    // cookie FIRST, even when an in-memory token exists. The cookie is shared
    // across every tab/window on this origin and always holds the LATEST
    // rotated token (the backend re-sets it on every successful
    // /auth/refresh); this tab's OWN in-memory token can be exactly one
    // rotation behind the instant ANOTHER tab refreshes first — the
    // `jq4-refresh` lock only serializes requests, it does not keep every
    // tab's in-memory copy in sync. Sending that stale token as the PRIMARY
    // attempt is what used to 401 tab B outright (or, combined with the
    // backend's reuse-grace-window recovery, could ping-pong the token across
    // tabs and eventually trip real reuse detection, revoking the whole
    // session). The in-memory token is now only a FALLBACK, tried when the
    // cookie-first attempt fails with an auth-shaped (401-family) rejection
    // AND a token is still held — covers e.g. a genuinely missing/blocked
    // cookie (third-party storage partitioning, Safari ITP) while this tab's
    // own token is still live. On an 'unreachable' cookie failure, there is
    // no point trying the token against the same unreachable backend.
    try {
        const next = await refreshWithRetry({}, BOOTSTRAP_TIMEOUT_MS);
        return installRefreshedSession(next);
    }
    catch (cookieErr) {
        if (classifyBootstrapFailure(cookieErr) === 'unreachable' || !sentRefreshToken) {
            return handleRefreshFailure(cookieErr, sentRefreshToken);
        }
        try {
            const next = await refreshWithRetry({ refreshToken: sentRefreshToken }, TOKEN_REFRESH_TIMEOUT_MS);
            return installRefreshedSession(next);
        }
        catch (tokenErr) {
            return handleRefreshFailure(tokenErr, sentRefreshToken);
        }
    }
}
async function performRefresh() {
    // MEDIUM fix (adversarial review) — a pending logout (queued because
    // `revokeRefreshToken` could not reach the network at signOut time) MUST
    // be flushed BEFORE any cookie-fallback refresh is even attempted —
    // otherwise a 401 firing right after reconnect would silently recover
    // and reinstall the very session the operator already asked to leave.
    // This runs inside the SAME single-flight/lock as every other refresh
    // attempt (see `runRefresh`), so it can never race a legitimate refresh.
    if (hasPendingLogout()) {
        // HIGH fix (finding #1) — the flag may have been armed for a DIFFERENT,
        // now-superseded session. `setSession`/`authStore.set` already clear it
        // the instant a new session installs (see auth-store.ts); this is the
        // second line of defense for when that clear itself loses a race (e.g.
        // a flush was already past this check in another tab/frame). Compare
        // the target `sid` the flag was armed for against whatever the store
        // holds RIGHT NOW: a mismatch means a newer, unrelated session is live
        // — drop the stale flag and fall through to a NORMAL refresh instead of
        // revoking it.
        const target = getPendingLogoutTarget();
        const current = authStore.get();
        const isStaleTarget = target?.sid != null && current?.sid != null && current.sid !== target.sid;
        if (!isStaleTarget) {
            // `rawRefresh`, not the default `runRefresh` — we are ALREADY inside
            // runRefresh()'s single-flight (this function IS what it invokes);
            // recovering via runRefresh() again here would deadlock (see
            // attemptRevoke's doc comment).
            const result = await attemptRevoke(authStore.getRefreshToken(), rawRefresh);
            if (result === 'retry') {
                markPendingLogout(target?.sid ?? null);
                return { kind: 'unreachable' };
            }
            clearPendingLogout();
            return { kind: 'unauthenticated' };
        }
        clearPendingLogout();
    }
    return rawRefresh();
}
async function runRefreshWithLock(locks) {
    return withTimeout(REFRESH_LOCK_WAIT_TIMEOUT_MS, async (signal) => {
        try {
            return await locks.request('jq4-refresh', { signal }, 
            // lib.dom's LockGrantedCallback<T> types the callback as returning
            // `T` synchronously — it does not model an async callback
            // returning `Promise<T>`, even though the real Web Locks API
            // explicitly holds the lock until that promise settles. The
            // runtime behavior is correct (a nested promise auto-flattens per
            // the Promise resolution procedure); this cast only corrects the
            // type.
            () => performRefresh());
        }
        catch {
            // HIGH fix (finding #2) — the lock was never granted before `signal`
            // aborted (another tab's attempt is stuck past the wait bound). This
            // is reachability-shaped, never a definitive auth rejection — must
            // never expire()/wipe a session.
            return { kind: 'unreachable' };
        }
    });
}
function runRefresh() {
    if (refreshInflight)
        return refreshInflight;
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    const attempt = locks?.request
        ? () => runRefreshWithLock(locks)
        : performRefresh;
    refreshInflight = attempt().finally(() => {
        refreshInflight = null;
    });
    return refreshInflight;
}
/** Exposes the full discriminated outcome — see BootstrapOutcome above. */
export function bootstrapSessionOutcome() {
    return runRefresh();
}
// Unchanged public contract (still used by apps/admin/src/main.tsx) —
// implemented in terms of runRefresh() so every entry point shares the
// same in-flight de-duplication.
export function bootstrapSession() {
    return runRefresh().then((outcome) => (outcome.kind === 'session' ? outcome.session : null));
}
// Wires the api-client's request layer to the Zustand auth store. Call
// once during app bootstrap (admin/main.tsx, pwa/main.tsx); idempotent.
export function setupAuthClient(opts) {
    if (configured)
        return;
    configured = true;
    configureClient({
        baseUrl: opts?.baseUrl,
        authBaseUrl: opts?.authBaseUrl,
        getAccessToken: () => authStore.getAccessToken(),
        // pwa-offline-first Slice 7 adversarial review, CRITICAL fix (now folded
        // into the HIGH single-flight fix above) — a caller can reach here with
        // an EMPTY in-memory auth store (the offline-shell path: the app booted
        // with `bootstrapSessionOutcome()` returning 'unreachable', so
        // `_authenticated.tsx` rendered from the cached offline profile with no
        // session at all). `runRefresh()`/`performRefresh()` decide themselves
        // whether to spend the in-memory token or fall back to the httpOnly
        // refresh cookie — this callback no longer needs to branch on it, and
        // (HIGH fix) shares ONE de-duplicated in-flight attempt with every other
        // caller (the offline-shell reconnect recovery hook, a parallel 401 on a
        // different request, etc.) regardless of which path they'd have taken.
        refresh: async () => {
            const outcome = await runRefresh();
            return outcome.kind === 'session' ? outcome.session.accessToken : null;
        },
        // HIGH fix — expiry is now decided ENTIRELY inside `performRefresh()`,
        // which compares the token it just spent against whatever the store
        // holds NOW before wiping anything (never a newer session a winning
        // parallel refresh already installed). Calling `authStore.expire()`
        // unconditionally here AGAIN, on top of that, would silently
        // reintroduce the exact "loser wipes winner" race this fix closes — so
        // this handler is deliberately a no-op.
        onSessionExpired: () => { },
    });
}
export function useAuth() {
    const session = useAuthStore((s) => s.session);
    const sessionExpired = useAuthStore((s) => s.sessionExpired);
    const sessionExpiredReason = useAuthStore((s) => s.sessionExpiredReason);
    const setSession = useAuthStore((s) => s.setSession);
    const clearSessionExpired = useAuthStore((s) => s.clearSessionExpired);
    async function expireSession() {
        // HIGH fix (finding #1) — capture the sid of the session actually being
        // logged out BEFORE clearing it, so a network-unreachable revoke can tag
        // the pending-logout flag with the right target (see performRefresh's
        // flush logic above).
        const current = authStore.get();
        const refreshToken = current?.refreshToken ?? null;
        const sid = current?.sid ?? null;
        authStore.expire();
        await revokeRefreshToken(refreshToken, sid);
    }
    async function signOut() {
        const current = authStore.get();
        const refreshToken = current?.refreshToken ?? null;
        const sid = current?.sid ?? null;
        authStore.signOut();
        await revokeRefreshToken(refreshToken, sid);
    }
    return {
        session,
        isAuthenticated: session !== null,
        sessionExpired,
        sessionExpiredReason,
        setSession,
        clearSessionExpired,
        expireSession,
        signOut,
    };
}
export function useMfaIntent() {
    const mfaIntent = useAuthStore((s) => s.mfaIntent);
    const setMfaIntent = useAuthStore((s) => s.setMfaIntent);
    return { mfaIntent, setMfaIntent };
}
//# sourceMappingURL=use-auth.js.map